// Imports
// *****************************************************
import {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { HeaderInterface } from 'src/types/header-types';
import { useInView } from 'react-intersection-observer';
import { AlertMessage } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { TopBannersHub } from '@components/features/banners';
import { SmartAppBanner } from '@components/features/smart-app-banner/SmartAppBanner';
import { MainNavbar } from './MainNavbar/MainNavbar';
import { useHeaderInfoData } from './MainNavbar/data/useHeaderInfoData';
import { setMenuTopOffset } from './MainNavbar/config';


/**
 * @description Header component. If you want to render banners that are not sticky, just place them
 * outside the header tag. Anything placed inside the header tag will be sticky.
 * * **navbarTheme** - the theme of the navbar. Can be 'default' or 'transparent' atm.
 *
 * @example
 * // Place a non-sticky alert or banner at the top of the page.
 * // It will not scroll down with the header.
 * <>
 *   <Banner />
 *   <Header ... />
 * </>
 *
 * // Place a sticky alert or sticky banner. It will scroll down with the header.
 * <Header ... >
 *   <AlertMessage />
 *   <MainNavbar />
 * </Header>
 *
 * // Place a search bar inside the header
 * <Header ... >
 *   <MainNavbar />
 *   <SearchBar />
 * </Header>
 *
 */
export const Header = (props: HeaderInterface) => {
  // Main component props with default values
  const { navbarTheme, navbarType = 'main', withoutAlert } = props;

  // translate
  const { t } = useTranslation('common');

  const headerRef = useRef<HTMLDivElement>(null);
  const dummyHeaderRef = useRef<HTMLDivElement>(null);
  const unpaidAlertRef = useRef<HTMLDivElement>(null);
  // Detect if the header is pinned (stuck) or not
  // This hook also creates a ref for the pinned element, in our case the header. If needed, you can merge
  // the refs using the 'mergeRefs' function
  // *********************************************************************
  const [pinnedRef, notPinned] = useInView({
    threshold: 1,
  });

  // Need to use a setter since we have two refs attached to the header
  // One is for the intersection observer, and the other is for the header element itself.
  const setRefs = (node: HTMLDivElement | null) => {
    headerRef.current = node;
    pinnedRef(node);
  };


  // Get current route; needed to highlight the current route in the menu for React pages
  // Get current locale
  // *********************************************************************
  const { pathname, locale } = useExtendedRouter();

  // Get the header data
  // The data is not language dependent, but I agreed with Levi that it's safer to do it anyway,
  // because the data is not that big, and it's not like the users are changing the language every minute.
  // *********************************************************************
  const {
    headerInfoData, headerInfoLoading, headerInfoValidating, isSessionAuthenticated,
  } = useHeaderInfoData(locale);

  const [unpaidOrderMessage, setUnpaidOrderMessage] = useState<string>('');
  const [unpaidTopUpMessage, setUnpaidTopUpMessage] = useState<string>('');

  // MainNavbar props. Just destructure it on component
  // *********************************************************************
  const mainNavbarProps = {
    headerInfo: headerInfoData,
    headerInfoLoading: headerInfoLoading && !isSessionAuthenticated,
    headerInfoValidating,
    navbarTheme,
    currentRoute: pathname,
    notPinned,
    locale,
  };

  useEffect(() => {
    if (!headerInfoData?.ordersNotificationData || !headerInfoData?.ordersNotificationData?.total) {
      return;
    }

    // unpaid `top-up` message has priority over unpaid `orders` message
    if (headerInfoData?.ordersNotificationData?.type === 'top-up') {
      let unpaidMessage = t('complex-unpaid.topup.stripe');
      unpaidMessage = unpaidMessage
        .replace('%amount_neuro%', headerInfoData?.ordersNotificationData?.totalCredits?.toString() ?? '');
      if (headerInfoData?.ordersNotificationData?.amountCurrencyGroups?.length) {
        unpaidMessage = unpaidMessage
          .replace('%amount_currency%', headerInfoData?.ordersNotificationData?.amountCurrencyGroups[0]);
      }

      setUnpaidOrderMessage('');
      setUnpaidTopUpMessage(unpaidMessage);
      return;
    }

    // unpaid `orders` message
    let unpaidMessage = headerInfoData?.ordersNotificationData?.total > 1 ? t('complex-unpaid.multi-invoice.stripe') : t('complex-unpaid.invoice.stripe');
    if (headerInfoData.ordersNotificationData?.type === 'negative-balance') {
      unpaidMessage = t('complex-unpaid.negative-balance.stripe');
    }

    unpaidMessage = unpaidMessage
      .replace('%nr%', String(headerInfoData?.ordersNotificationData?.total))
      .replace('%due_date%', headerInfoData?.ordersNotificationData?.oldestDueDate ?? '')
      .replace('%amount_neuro%', headerInfoData?.ordersNotificationData?.totalCredits?.toString() ?? '');
    if (headerInfoData?.ordersNotificationData?.amountCurrencyGroups?.length) {
      unpaidMessage = unpaidMessage
        .replace('%amount_currency%', headerInfoData?.ordersNotificationData?.amountCurrencyGroups[0]);
    }

    setUnpaidTopUpMessage('');
    setUnpaidOrderMessage(unpaidMessage);
  }, [headerInfoData?.ordersNotificationData, t]);

  // Set the dummy header height to the height of the fixed header, so none of the pages are affected since previously it was using sticky.
  // useLayoutEffect is used here to ensure that the height is set before the first paint.
  useLayoutEffect(() => {
    if (!headerRef.current) return undefined;

    let animationFrameId: number | undefined;

    const setDummyHeaderHeight = () => {
      if (!dummyHeaderRef.current) return;

      const height = headerRef.current?.offsetHeight ?? 0;
      dummyHeaderRef.current.style.height = `${height}px`;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    };

    const scheduleDummyHeaderHeight = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = undefined;
        setDummyHeaderHeight();
      });
    };

    setDummyHeaderHeight();

    const observer = new ResizeObserver(scheduleDummyHeaderHeight);
    observer.observe(headerRef.current);

    return () => {
      observer.disconnect();
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      document.body.style.paddingTop = '';
      document.documentElement.style.removeProperty('--header-height');
    };
  }, []);

  // Track the unpaid alert height so the sidebar can start below it.
  useEffect(() => {
    let animationFrameId: number | undefined;

    const updateAlertHeight = () => {
      const height = unpaidAlertRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty('--unpaid-alert-height', `${height}px`);
    };

    const scheduleAlertHeightUpdate = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = undefined;
        updateAlertHeight();
      });
    };

    updateAlertHeight();

    const observer = new ResizeObserver(scheduleAlertHeightUpdate);
    if (unpaidAlertRef.current) observer.observe(unpaidAlertRef.current);

    return () => {
      observer.disconnect();
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      document.documentElement.style.setProperty('--unpaid-alert-height', '0px');
    };
  }, [unpaidOrderMessage, unpaidTopUpMessage]);


  // Calculate header height + offset for the menu. When having banners or alerts,
  // with show/hide conditions, add them to the dependency array.
  useEffect(() => {
    setMenuTopOffset();
  }, [headerInfoData, unpaidOrderMessage]);


  // Render the Header
  // *********************************************************************
  return (
    <>
      <header id="header" ref={setRefs} className="fixed inset-x-0 -top-px z-10 print:hidden">
        <SmartAppBanner />
        { !withoutAlert && <TopBannersHub /> }

        {/* Unpaid alert messages — wrapped in a measured div so --unpaid-alert-height
            stays current and the sidebar can start exactly below them */}
        {!withoutAlert && (
          <div ref={unpaidAlertRef}>
            {unpaidOrderMessage !== '' && (
              <AlertMessage
                urlPath="app_order_list"
                symfonyRoute
                symfonyParams={{ unpaid: 1 }}
                message={unpaidOrderMessage}
                type="error"
                ctaText={t('global.alert.unpaid.invoices.action')}
              />
            )}
            {unpaidTopUpMessage !== '' && (
              <AlertMessage
                message={unpaidTopUpMessage}
                type="info"
              />
            )}
          </div>
        )}
        {/* Main Navbar */}
        {navbarType === 'main' && <MainNavbar {...mainNavbarProps} />}
      </header>
      {/* Dummy header for replicating header height to not alter any of the layouts by switching to a fixed header */}
      <div ref={dummyHeaderRef} className="print:hidden" />
    </>
  );
};
